// components/patient/VoiceRecorder.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  MicrophoneIcon,
  StopIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, transcript: string) => void;
  onCancel: () => void;
  existingContent?: string;
  maxDuration?: number; // in seconds
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onCancel,
  existingContent = '',
  maxDuration = 300, // 5 minutes
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    // Check for browser support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Voice recording is not supported in this browser');
      return;
    }

    // Initialize speech recognition if available
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(prev => prev + finalTranscript);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech') {
          setError('Speech recognition failed. You can still use the audio recording.');
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        setAudioBlob(blob);
        
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingDuration(0);

      // Start speech recognition
      if (recognitionRef.current) {
        setTranscript('');
        setIsTranscribing(true);
        try {
          recognitionRef.current.start();
        } catch (e) {
          // Speech recognition already started or not available
        }
      }

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const newDuration = prev + 1;
          if (newDuration >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newDuration;
        });
      }, 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    if (recognitionRef.current && isTranscribing) {
      recognitionRef.current.stop();
      setIsTranscribing(false);
    }

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  const playRecording = () => {
    if (audioURL && !isPlaying) {
      const audio = new Audio(audioURL);
      audioPlayerRef.current = audio;
      
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => {
        setError('Failed to play recording');
        setIsPlaying(false);
      };
      
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.error('Playback failed:', err);
          setError('Failed to play recording');
        });
    }
  };

  const pausePlayback = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    
    setAudioBlob(null);
    setAudioURL(null);
    setTranscript('');
    setRecordingDuration(0);
    setIsPlaying(false);
    
    if (audioPlayerRef.current) {
      audioPlayerRef.current = null;
    }
  };

  const handleComplete = () => {
    if (audioBlob) {
      onRecordingComplete(audioBlob, transcript);
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getDurationColor = (): string => {
    const percentage = (recordingDuration / maxDuration) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-gray-900';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Voice Recording</h3>
          <button
            onClick={onCancel}
            className="text-gray-700 hover:text-gray-900"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Recording interface */}
        <div className="text-center mb-6">
          {/* Recording button */}
          <div className="mb-4">
            {!isRecording && !audioBlob ? (
              <button
                onClick={startRecording}
                disabled={!!error}
                className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <MicrophoneIcon className="h-8 w-8" />
              </button>
            ) : isRecording ? (
              <button
                onClick={stopRecording}
                className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white animate-pulse"
              >
                <StopIcon className="h-8 w-8" />
              </button>
            ) : (
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={playRecording}
                  disabled={isPlaying}
                  className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {isPlaying ? (
                    <PauseIcon className="h-6 w-6" onClick={pausePlayback} />
                  ) : (
                    <PlayIcon className="h-6 w-6" />
                  )}
                </button>
                
                <button
                  onClick={deleteRecording}
                  className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-700"
                >
                  <TrashIcon className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>

          {/* Duration display */}
          <div className={`text-2xl font-mono ${getDurationColor()}`}>
            {formatDuration(recordingDuration)}
          </div>
          
          {/* Max duration warning */}
          {recordingDuration > maxDuration * 0.8 && (
            <p className="text-sm text-yellow-600 mt-2">
              {maxDuration - recordingDuration} seconds remaining
            </p>
          )}

          {/* Status text */}
          <p className="text-sm text-gray-900 mt-2">
            {isRecording 
              ? 'Recording... Speak clearly into your microphone'
              : audioBlob 
                ? 'Recording complete. Review and confirm below.'
                : 'Click the microphone to start recording'
            }
          </p>
        </div>

        {/* Transcript display */}
        {(transcript || isTranscribing) && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Live Transcript {isTranscribing && '(listening...)'}
            </h4>
            <div className="p-3 bg-gray-50 rounded-lg border min-h-[80px] max-h-[120px] overflow-y-auto">
              <p className="text-sm text-gray-700">
                {transcript || (isTranscribing ? 'Listening for speech...' : 'No speech detected yet')}
              </p>
            </div>
            <p className="text-xs text-gray-800 mt-1">
              The transcript will be added to your existing feedback
            </p>
          </div>
        )}

        {/* Existing content preview */}
        {existingContent && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Current Feedback</h4>
            <div className="p-3 bg-blue-50 rounded-lg border max-h-[100px] overflow-y-auto">
              <p className="text-sm text-gray-700">
                {existingContent.length > 200 
                  ? `${existingContent.substring(0, 200)}...`
                  : existingContent
                }
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-900 hover:text-gray-800"
          >
            Cancel
          </button>

          <div className="flex items-center space-x-3">
            {audioBlob && (
              <button
                onClick={() => {
                  deleteRecording();
                  // Restart recording
                  setTimeout(startRecording, 100);
                }}
                className="px-4 py-2 text-blue-600 hover:text-blue-700"
              >
                Re-record
              </button>
            )}
            
            <button
              onClick={handleComplete}
              disabled={!audioBlob}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Use Recording
            </button>
          </div>
        </div>

        {/* Recording tips */}
        {!isRecording && !audioBlob && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h5 className="text-sm font-medium text-blue-900 mb-2">Recording Tips:</h5>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Speak clearly and at a normal pace</li>
              <li>• Find a quiet environment to reduce background noise</li>
              <li>• The transcript will be added to your written feedback</li>
              <li>• Maximum recording time: {formatDuration(maxDuration)}</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Add type declarations for Speech Recognition API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

export default VoiceRecorder;